import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, User, Gift, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";

const LOGO_LANDSCAPE = "/assets/logo-landscape.png";
const LOGO_SQUARE = "/assets/logo-square.png";

/**
 * Sanitize a `?next=<url>` param before navigating to it. Accepts
 * only *relative* internal paths — must start with a single `/` and
 * not `//` (which the browser would resolve as protocol-relative to
 * a foreign host). Prevents open-redirect via crafted share links.
 * Returns null when input is unsafe or empty.
 */
function _safeNextUrl(raw) {
  if (!raw) return null;
  let s;
  try {
    s = decodeURIComponent(String(raw));
  } catch {
    return null;
  }
  if (!s.startsWith("/") || s.startsWith("//")) return null;
  // Also block back-to-auth loops so a stray `?next=/auth` doesn't
  // create an infinite login redirect.
  if (s === "/auth" || s.startsWith("/auth?") || s.startsWith("/auth/")) return null;
  return s;
}

/** Returns true when the viewport is wide enough for the desktop split layout.
 *  Updates on resize so test runners and live-resizes both see the right tree. */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
  );
  useEffect(() => {
    const mql = window.matchMedia("(min-width: 1024px)");
    const onChange = (e) => setIsDesktop(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

export default function AuthPage() {
  const { login, register, loginWithGoogle, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isDesktop = useIsDesktop();
  const refFromUrl = (params.get("ref") || "").toLowerCase();
  const initialMode = params.get("mode") === "register" || refFromUrl ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [referrer, setReferrer] = useState(null);
  // Default to "remember me" ON — feels like the expected default for most users.
  const [remember, setRemember] = useState(() => {
    const stored = localStorage.getItem("tb_remember");
    return stored === null ? true : stored === "true";
  });

  useEffect(() => {
    const code = refFromUrl || localStorage.getItem("tb_ref_code");
    if (!code) return;
    localStorage.setItem("tb_ref_code", code);
    (async () => {
      try {
        const { data } = await api.get(`/referral/${code}`);
        if (data.valid) setReferrer(data);
      } catch (err) {
        // Invalid/expired referral codes silently fall back to plain signup.
        console.warn("[AuthPage] referral lookup failed:", err);
      }
    })();
  }, [refFromUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      let signedIn;
      if (mode === "login") {
        localStorage.setItem("tb_remember", String(remember));
        signedIn = await login(email, password, remember);
      } else {
        signedIn = await register(email, password, name);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Welcome to TravelBio!");
      // Post-auth destination priority:
      //   1) `?next=<url>` on the auth page (set by <Protected/> when
      //      redirecting to /auth). Restores deep-links like
      //      `/explore?card=<id>` from a shared preview URL.
      //   2) Fallback: admins → /admin, everyone else → /home.
      // `next` is sanitized to relative paths only (no scheme, no `//`)
      // to close open-redirect risk.
      const rawNext = params.get("next") || "";
      const nextUrl = _safeNextUrl(rawNext);
      if (nextUrl) {
        try { window.sessionStorage.removeItem("tb_post_auth_next"); } catch { /* noop */ }
        nav(nextUrl, { replace: true });
      } else {
        nav(signedIn?.role === "admin" ? "/admin" : "/home");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * The full body of the auth panel — welcome header + form + footer tagline.
   * Declared as a JSX expression (not a nested component) so React reuses
   * the same elements in both layouts without losing input focus on rerender.
   * The desktop wrapper (`.tb-auth-dark`) re-colors the dark labels to white
   * via CSS so the navy background reads correctly.
   */
  const formBody = (
    <>
      <div className="text-center mb-6">
        <p className="font-hand text-tb-blue text-xl italic">
          ~ {mode === "login" ? "welcome back, wanderer" : "begin your journey"} ~
        </p>
        <h2 className="font-serif-it text-tb-navy text-3xl mt-1">
          {mode === "login" ? "Open Your Journal" : "Create Your Journal"}
        </h2>
      </div>

      {referrer && mode === "register" && (
        <div data-testid="auth-referrer-badge"
          className="mb-4 rounded-2xl bg-tb-coral-soft border-2 border-tb-coral/40 px-4 py-2.5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-tb-coral text-white flex items-center justify-center shrink-0">
            <Gift className="w-4 h-4" />
          </div>
          <div className="text-sm">
            <span className="font-serif-it text-tb-navy text-base">{referrer.name}</span>
            <span className="font-hand text-tb-navy/70"> invited you · they&apos;ll earn </span>
            <b className="text-tb-coral">+250 pts</b>
            <span className="font-hand text-tb-navy/70"> when you join</span>
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        {mode === "register" && (
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tb-navy/50" />
            <input
              data-testid={AUTH.nameInput}
              type="text"
              required
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-tb-navy/15 focus:border-tb-navy focus:outline-none focus:ring-2 focus:ring-tb-navy/10 font-ui text-tb-navy placeholder:text-tb-navy/40"
            />
          </div>
        )}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tb-navy/50" />
          <input
            data-testid={AUTH.emailInput}
            type="email"
            required
            placeholder="email@traveler.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-tb-navy/15 focus:border-tb-navy focus:outline-none focus:ring-2 focus:ring-tb-navy/10 font-ui text-tb-navy placeholder:text-tb-navy/40"
          />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-tb-navy/50" />
          <input
            data-testid={AUTH.passwordInput}
            type={showPassword ? "text" : "password"}
            required
            minLength={6}
            placeholder="password (min 6)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full pl-10 pr-11 py-3 rounded-xl bg-white border border-tb-navy/15 focus:border-tb-navy focus:outline-none focus:ring-2 focus:ring-tb-navy/10 font-ui text-tb-navy placeholder:text-tb-navy/40"
          />
          <button
            type="button"
            data-testid="auth-password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-tb-navy/50 hover:text-tb-navy transition-colors"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {mode === "login" && (
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input
              data-testid="auth-remember-checkbox"
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-tb-coral cursor-pointer"
            />
            <span className="font-hand text-tb-navy/75 text-sm">
              Remember me on this device
            </span>
          </label>
        )}

        {mode === "login" && (
          <a
            data-testid="login-forgot-password-link"
            href="/forgot-password"
            className="block text-tb-navy/70 font-hand text-sm hover:text-tb-navy underline underline-offset-2 self-start"
          >
            Forgot password?
          </a>
        )}

        {err && (
          <p data-testid={AUTH.error} className="text-tb-coral font-hand text-base">{err}</p>
        )}

        <button
          data-testid="login-submit"
          type="submit"
          disabled={loading}
          className="w-full py-3 rounded-xl bg-tb-navy text-tb-gold-bright font-hand text-xl border-2 border-tb-gold tb-tap hover:bg-tb-navy-soft disabled:opacity-60"
        >
          {loading ? "..." : mode === "login" ? "Open Journal →" : "Start Journal ✨"}
        </button>

        <div className="flex items-center gap-2 my-1">
          <span className="h-px flex-1 bg-tb-cream-3" />
          <span className="font-hand text-tb-navy/50 text-sm">or</span>
          <span className="h-px flex-1 bg-tb-cream-3" />
        </div>

        <button
          data-testid={AUTH.googleBtn}
          type="button"
          onClick={loginWithGoogle}
          className="w-full py-3 rounded-xl bg-white text-tb-navy font-ui font-semibold text-base border-2 border-tb-cream-3 hover:border-tb-navy/40 tb-tap flex items-center justify-center gap-3"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
            <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
            <path fill="#FBBC05" d="M11.69 28.18A13.78 13.78 0 0 1 10.97 24c0-1.45.25-2.86.72-4.18v-5.7H4.34A22 22 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
            <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7C13.42 14.62 18.27 10.75 24 10.75z"/>
          </svg>
          Continue with Google
        </button>

        <button
          data-testid={AUTH.toggleModeBtn}
          type="button"
          onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
          className="w-full text-tb-navy/70 hover:text-tb-coral font-hand text-base"
        >
          {mode === "login"
            ? "New here? Create a journal →"
            : "← Already have a journal? Log in"}
        </button>
      </form>

      <p className="text-center font-hand tb-creative text-sm italic text-tb-navy/55 mt-6 tracking-wide">
        your lifelong travel journal
      </p>
    </>
  );

  return (
    <div className="min-h-screen w-full">
      {/* --- Mobile / tablet — original phone-frame card layout.
              2026-07-17: outer wrapper + form body switched from
              navy / cream to white so the auth screen reads clean
              against the app's new white palette. Navy logo header
              strip preserved for brand continuity. --- */}
      {!isDesktop && (
      <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-white">
        <div className="tb-frame w-full max-w-md md:rounded-[2rem] overflow-hidden bg-white">
          <div
            className="border-b-2 border-tb-gold/70 flex items-center px-3"
            style={{ height: 96, backgroundColor: "#061c37" }}
          >
            <img
              src={LOGO_LANDSCAPE}
              alt="TravelBio"
              width={340}
              height={84}
              loading="eager"
              className="block select-none"
              style={{ height: 84, width: "auto" }}
              draggable={false}
            />
          </div>
          <div className="bg-white px-6 py-8">
            {formBody}
          </div>
        </div>
      </div>
      )}

      {/* --- Desktop split-screen: navy compass on left, mobile-style card on right.
              Both halves share `#11233E` at the top so the navy band flows
              seamlessly across the whole viewport width — no vertical seam
              visible in the header row. Below the header, the right column
              switches to the cream form body while the left stays navy for
              the full column height. --- */}
      {isDesktop && (
      <div className="grid grid-cols-2 min-h-screen">
        {/* Left half: solid navy with the compass icon centered.
            The `icon-preview.png` is the app-icon compass on the exact same
            `#11233E` navy backdrop, so it composites onto the column with
            zero color mismatch. */}
        <div
          className="flex items-center justify-center"
          style={{ backgroundColor: "#11233E" }}
          data-testid="auth-desktop-left-compass"
        >
          <img
            src="/assets/icon-preview.png"
            alt="TravelBio compass"
            className="block select-none"
            style={{ width: "40%", height: "auto" }}
            draggable={false}
          />
        </div>

        {/* Right half: replicates the mobile card exactly — navy header
            strip with the landscape logo, then the cream form body. The
            top strip's `#11233E` matches the left column so the two halves
            read as a single navy band at the top. */}
        <div className="flex flex-col min-h-screen">
          <div
            className="border-b-2 border-tb-gold/70 flex items-center justify-center px-3 shrink-0"
            style={{ height: 96, backgroundColor: "#11233E" }}
          >
            <img
              src={LOGO_LANDSCAPE}
              alt="TravelBio"
              width={340}
              height={84}
              loading="eager"
              className="block select-none"
              style={{ height: 84, width: "auto" }}
              draggable={false}
            />
          </div>
          <div className="flex-1 bg-white flex items-center justify-center px-8 py-10">
            <div className="w-full max-w-md">
              {formBody}
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
}
